// Settings → Service area (0330).
//
// How far from a hub a client may book. DIST-009 approves 15 km; Kharie made it
// editable on 2026-10-14, so any other value is a deliberate exception that
// Luigi has to confirm — the warning below says so rather than hiding it.
//
// The last surcharge band moves with this number (a database trigger), so the
// Distance card below always ends where the service area does. The database
// refuses a limit at or inside where that last band starts: removing a band is
// a pricing decision, not a side effect of this field.

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  type DistanceBand,
  type OperatingSettings,
  useUpdateOperatingSettings,
} from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";

const APPROVED_LIMIT_KM = 15;

export function ServiceAreaCard({
  settings,
  bands,
}: {
  settings: OperatingSettings;
  bands: readonly DistanceBand[];
}) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState(() => String(settings.service_area_limit_km));

  useEffect(() => {
    setDraft(String(settings.service_area_limit_km));
  }, [settings]);

  // Where the last band starts — the limit must stay further out (0330).
  const lastBandStartKm = bands.length > 0 ? Math.max(...bands.map((band) => band.min_km)) : 0;
  const parsed = Number(draft);
  const valid = Number.isFinite(parsed) && parsed > lastBandStartKm && parsed <= 999;

  function save() {
    if (!valid) {
      toast.error(`The service area has to be more than ${lastBandStartKm} km.`);
      return;
    }
    update.mutate(
      { service_area_limit_km: parsed },
      {
        onSuccess: () => toast.success("Service area saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader className="gap-2">
        <CardTitle className="type-title-body"><h2>Service area</h2></CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          How far from a hub a client may book, by road. Past it the booking is refused and the
          client is asked for a closer address. A hub never reaches further than its own radius, set
          on the hub&apos;s page.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5">
        <Field label={`Limit (km, more than ${lastBandStartKm})`}>
          <NumberInput value={draft} onChange={setDraft} step="0.5" />
        </Field>
        {valid && parsed !== APPROVED_LIMIT_KM ? (
          <p className="max-w-prose text-sm leading-relaxed text-warning">
            {APPROVED_LIMIT_KM} km is the approved service area. {parsed} km is a deliberate
            exception, and the last distance band will cover {lastBandStartKm}–{parsed} km at its
            current rate.
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending || !valid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}
